import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import type { TeamRole } from '@/shared/services/team.service';
import { canReadAnalytics, canReadInbox } from '@/shared/lib/role-access';

/** One destination of the app menu. */
export interface NavLeaf {
  key: string;
  path: string;
  icon: LucideIcon;
  labelKey: string;
  /** A page that is one tab of a route (`?tab=`): Library's post and reply templates. */
  tab?: string;
  /** The tab shown when the address names none. */
  defaultTab?: boolean;
}

/** Where a menu leaf links to, its tab included. */
export function leafHref(leaf: NavLeaf): string {
  return leaf.tab ? `${leaf.path}?tab=${encodeURIComponent(leaf.tab)}` : leaf.path;
}

/** Whether a leaf whose route matched is the one shown: for a tab page, the tab must match too. */
export function leafActive(leaf: NavLeaf, routeActive: boolean, search: string): boolean {
  if (!routeActive || !leaf.tab) return routeActive;
  const tab = new URLSearchParams(search).get('tab');
  return tab === leaf.tab || (tab === null && leaf.defaultTab === true);
}

/**
 * A top-level entry. With `children` it is a folder: it opens in place to
 * show its pages, and its own link goes to the first child. Every page the
 * app had keeps its entry — the menu only nests them (UI v2: fewer things to
 * read at the top level, nothing removed).
 */
export interface NavEntry extends NavLeaf {
  children?: NavLeaf[];
}

export interface NavSection {
  key: string;
  entries: NavEntry[];
}

export const NAV_ITEM = {
  overview: { key: 'overview', path: ROUTES.OVERVIEW, icon: Icons.home, labelKey: 'nav.overview' },
  inbox: { key: 'inbox', path: ROUTES.INBOX, icon: Icons.inbox, labelKey: 'nav.inbox' },
  calendar: { key: 'calendar', path: ROUTES.CALENDAR, icon: Icons.calendar, labelKey: 'nav.calendar' },
  schedule: { key: 'schedule', path: ROUTES.SCHEDULE, icon: Icons.calendar, labelKey: 'nav.schedule' },
  plan: { key: 'plan', path: ROUTES.PLAN, icon: Icons.plan, labelKey: 'nav.plan' },
  posts: { key: 'posts', path: ROUTES.POSTS, icon: Icons.posts, labelKey: 'nav.posts' },
  allPosts: { key: 'allPosts', path: ROUTES.POSTS, icon: Icons.posts, labelKey: 'nav.allPosts' },
  approvals: { key: 'approvals', path: ROUTES.APPROVALS, icon: Icons.check, labelKey: 'nav.approvals' },
  feeds: { key: 'feeds', path: ROUTES.FEEDS, icon: Icons.feeds, labelKey: 'nav.published' },
  grow: { key: 'grow', path: ROUTES.GROW, icon: Icons.trendUp, labelKey: 'nav.grow' },
  analytics: { key: 'analytics', path: ROUTES.ANALYTICS, icon: Icons.analytics, labelKey: 'nav.analytics' },
  library: { key: 'library', path: ROUTES.MEDIA, icon: Icons.media, labelKey: 'nav.groups.library' },
  media: { key: 'media', path: ROUTES.MEDIA, icon: Icons.media, labelKey: 'nav.media' },
  templates: { key: 'templates', path: ROUTES.TEMPLATES, icon: Icons.templates, labelKey: 'nav.templates' },
  postTemplates: { key: 'postTemplates', path: ROUTES.TEMPLATES, icon: Icons.templates, labelKey: 'nav.postTemplates', tab: 'posts', defaultTab: true },
  replyTemplates: { key: 'replyTemplates', path: ROUTES.TEMPLATES, icon: Icons.templates, labelKey: 'nav.replyTemplates', tab: 'replies' },
  ai: { key: 'ai', path: ROUTES.AI_OVERVIEW, icon: Icons.sparkles, labelKey: 'nav.ai' },
  aiMemory: { key: 'aiMemory', path: ROUTES.AI_MEMORY, icon: Icons.sparkles, labelKey: 'nav.aiMemory' },
  aiOverview: { key: 'aiOverview', path: ROUTES.AI_OVERVIEW, icon: Icons.sparkles, labelKey: 'nav.aiOverview' },
  ask: { key: 'ask', path: ROUTES.ASK, icon: Icons.sparkles, labelKey: 'nav.ask' },
  aiReplies: { key: 'aiReplies', path: ROUTES.INBOX_SETTINGS, icon: Icons.inbox, labelKey: 'nav.aiReplies' },
  brand: { key: 'brand', path: ROUTES.BRAND, icon: Icons.brand, labelKey: 'nav.brand' },
  channels: { key: 'channels', path: ROUTES.CHANNELS, icon: Icons.channels, labelKey: 'nav.channels' },
  settings: { key: 'settings', path: ROUTES.SETTINGS, icon: Icons.settings, labelKey: 'nav.settings' },
  docs: { key: 'docs', path: ROUTES.DOCS, icon: Icons.help, labelKey: 'nav.help' },
  admin: { key: 'admin', path: ROUTES.ADMIN, icon: Icons.admin, labelKey: 'nav.admin' },
} satisfies Record<string, NavLeaf>;

/**
 * The menu: daily work first (Home, Inbox, Calendar, Posts, Analytics), then
 * the AI assistant, Library and Channels. Calendar holds the weekly plan;
 * Posts holds approvals and the social feeds; the AI assistant holds Ask,
 * how it replies (the inbox settings) and what it knows (the brand
 * profile); Library holds media and templates.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'main',
    entries: [
      NAV_ITEM.overview,
      NAV_ITEM.inbox,
      { ...NAV_ITEM.calendar, children: [NAV_ITEM.calendar, NAV_ITEM.plan, NAV_ITEM.schedule] },
      { ...NAV_ITEM.posts, children: [NAV_ITEM.allPosts, NAV_ITEM.approvals, NAV_ITEM.feeds] },
      NAV_ITEM.grow,
      NAV_ITEM.analytics,
    ],
  },
  {
    key: 'setup',
    entries: [
      { ...NAV_ITEM.ai, children: [NAV_ITEM.aiOverview, NAV_ITEM.ask, NAV_ITEM.aiMemory, NAV_ITEM.aiReplies, NAV_ITEM.brand] },
      { ...NAV_ITEM.library, children: [NAV_ITEM.media, NAV_ITEM.postTemplates, NAV_ITEM.replyTemplates] },
      NAV_ITEM.channels,
    ],
  },
];

export const NAV_FOOTER: NavLeaf[] = [NAV_ITEM.settings, NAV_ITEM.docs];

/** A client gets a review portal: what waits for them, the calendar to read, the results (PC-26). */
export const CLIENT_SECTIONS: NavSection[] = [{ key: 'main', entries: [NAV_ITEM.approvals, NAV_ITEM.calendar, NAV_ITEM.analytics] }];
export const CLIENT_FOOTER: NavLeaf[] = [NAV_ITEM.docs];

/** Whether a role may open a destination, as the API enforces it (AL-06). */
export function navAllowed(key: string, role: TeamRole | null): boolean {
  // The AI pages read the inbox and its settings: the inbox roles.
  if (key === 'inbox' || key === 'ask' || key === 'aiReplies' || key === 'aiOverview' || key === 'aiMemory') return canReadInbox(role);
  if (key === 'analytics') return canReadAnalytics(role);
  return true;
}

/** The sections a role sees: forbidden pages dropped, a folder left with one page becomes that page. */
export function sectionsFor(role: TeamRole | null): NavSection[] {
  const base = role === 'client' ? CLIENT_SECTIONS : NAV_SECTIONS;
  return base
    .map((s) => ({
      ...s,
      entries: s.entries
        .map((e): NavEntry | null => {
          if (!e.children) return navAllowed(e.key, role) ? e : null;
          const children = e.children.filter((c) => navAllowed(c.key, role));
          if (children.length === 0) return null;
          if (children.length === 1) return children[0] as NavLeaf;
          return { ...e, path: (children[0] as NavLeaf).path, children };
        })
        .filter((e): e is NavEntry => e !== null),
    }))
    .filter((s) => s.entries.length > 0);
}

/**
 * Whether another menu destination sits below `path` (Inbox › /inbox/settings
 * is the AI assistant's Replies page): such an entry is active on its own
 * page only, so two entries are never lit at once.
 */
export function hasDeeperDestination(path: string): boolean {
  return Object.values(NAV_ITEM).some((i) => i.path !== path && i.path.startsWith(`${path}/`));
}

/** Whether `pathname` is inside a destination (its page or one below it). */
export function pathIn(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** `pathIn`, but a destination with another below it only matches its own page. */
export function inDestination(pathname: string, path: string): boolean {
  return hasDeeperDestination(path) ? pathname === path : pathIn(pathname, path);
}

/** The folder a pathname belongs to, if any: it opens on its own when one of its pages is shown. */
export function folderOf(pathname: string, sections: NavSection[]): string | null {
  for (const s of sections) {
    for (const e of s.entries) {
      if (e.children?.some((c) => inDestination(pathname, c.path))) return e.key;
    }
  }
  return null;
}
